import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Transaction } from '@solana/web3.js';

import { useSvmSendTransaction } from './useSvmSendTransaction';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import { useAuthContext } from '@/context/AuthContext';
import * as pointsLotteryApi from '@/api/pointsLottery';
import { isMobileWallet } from '@/lib/responsive';
import { logger } from '@/lib/logger';
import { parseSolanaError } from '@/utils/solanaErrorParser';
import type {
  LotteryHistoryResponse,
  LotteryLeaderboardResponse,
  LotterySeasonResponse,
  SeasonResultsResponse,
  UnclaimedRewardsResponse,
  UserNFTsResponse,
  ShareResultResponse,
  NFTMintResponse,
  NFTTier,
} from '@/types/schemas';

export function usePointsLotterySeason() {
  const { address } = useWalletAddress();
  const walletAddress = address ?? undefined;

  return useQuery<LotterySeasonResponse>({
    queryKey: ['pointsLottery', 'season', walletAddress ?? 'anon'],
    queryFn: () => pointsLotteryApi.getCurrentSeason(walletAddress),
    // Dynamic staleTime based on season phase for fresher data during transitions
    staleTime: (query) => {
      const phase = query.state.data?.season?.phase;
      if (phase === 'settling') return 5_000; // 5 seconds - very fresh during draw
      if (phase === 'active') return 15_000; // 15 seconds - fresh during active phase
      return 60_000; // 60 seconds - standard for warmup/complete
    },
    gcTime: 5 * 60_000, // 5 minutes
    // Dynamic refetch interval based on season phase for faster updates during transitions
    refetchInterval: (query) => {
      const phase = query.state.data?.season?.phase;
      if (phase === 'settling') return 5_000; // 5 seconds - very fast to see draw complete
      if (phase === 'active') return 15_000; // 15 seconds - fast for live lottery
      return 60_000; // 60 seconds - slower when nothing changing (warmup/complete)
    },
    refetchOnWindowFocus: false, // Disable to reduce API calls
    retry: (failureCount, error) => {
      // Don't retry if no season is active
      if (error instanceof Error && error.message.toLowerCase().includes('no active season')) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function usePointsLotteryHistory({
  enabled,
  season,
}: {
  enabled: boolean;
  season?: number;
}) {
  const { address } = useWalletAddress();
  const params = useMemo(() => ({
    season,
    wallet_address: address ?? undefined,
  }), [season, address]);

  return useQuery<LotteryHistoryResponse>({
    queryKey: ['pointsLottery', 'history', params],
    queryFn: () => pointsLotteryApi.getHistory(params),
    enabled,
    staleTime: 10_000, // Reduced from 60s to 10s to get fresher claim status
    refetchOnWindowFocus: true, // Always refetch when user returns to window
    retry: (failureCount, error) => {
      // Don't retry if no season is active
      if (error instanceof Error && error.message.toLowerCase().includes('no active season')) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function usePointsLotteryLeaderboard({ enabled }: { enabled: boolean }) {
  const { address } = useWalletAddress();

  return useQuery<LotteryLeaderboardResponse>({
    queryKey: ['pointsLottery', 'leaderboard', address ?? 'anon'],
    queryFn: () =>
      pointsLotteryApi.getLeaderboard({
        wallet_address: address ?? undefined,
      }),
    enabled,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      // Don't retry if no season is active
      if (error instanceof Error && error.message.toLowerCase().includes('no active season')) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useContributePoints() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (points: number) => {
      if (!Number.isFinite(points) || points <= 0) {
        throw new Error('Contribution must be a positive number');
      }
      const response = await pointsLotteryApi.contributePoints({
        points: Math.floor(points),
      });
      return response;
    },
    onMutate: async () => {
      // Optimistically start invalidating queries when mutation begins
      // This ensures faster UI updates even before the API call completes
      await queryClient.cancelQueries({ queryKey: ['pointsLottery'] });
      await queryClient.cancelQueries({ queryKey: ['userPoints'] });
    },
    onSuccess: () => {
      toast.success('Points contributed successfully!');
      queryClient.invalidateQueries({ queryKey: ['pointsLottery'] });
      queryClient.invalidateQueries({ queryKey: ['points', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['points', 'ledger'] });
      queryClient.invalidateQueries({ queryKey: ['userPoints'] }); // Invalidate navbar points display
    },
    onError: (error) => {
      // Parse error message for better user feedback
      let errorMessage = 'Contribution failed';
      if (error instanceof Error) {
        const msg = error.message.toLowerCase();
        if (msg.includes('not enough') || msg.includes('insufficient')) {
          errorMessage = 'Insufficient points available';
        } else if (msg.includes('in progress') || msg.includes('busy')) {
          errorMessage = 'Another contribution is in progress. Please wait.';
        } else if (msg.includes('not active') || msg.includes('not accepting')) {
          errorMessage = 'Lottery is not currently accepting contributions';
        } else {
          errorMessage = error.message;
        }
      }
      toast.error(errorMessage);
    },
    // Don't retry on failure - contribution errors are usually intentional (insufficient balance, etc.)
    retry: false,
  });
}

export function useClaimLotteryReward(options?: { showToast?: boolean }) {
  const queryClient = useQueryClient();
  const showToast = options?.showToast ?? true; // Default to true for backward compatibility

  return useMutation({
    mutationFn: async (seasonId?: number) => {
      console.log('[Hook] useClaimLotteryReward mutationFn called with seasonId:', seasonId);
      const payload = { season_id: seasonId };
      console.log('[Hook] Constructed payload object:', payload);
      console.log('[Hook] Calling pointsLotteryApi.claimLotteryReward with payload:', JSON.stringify(payload));

      const response = await pointsLotteryApi.claimLotteryReward(payload);
      return response;
    },
    onSuccess: (data) => {
      console.log('[Hook] Claim SUCCESS - invalidating all lottery queries');
      // Only show toast if explicitly enabled (defaults to true)
      if (showToast) {
        toast.success(`Reward claimed successfully! ${data.data?.prizeLabel || ''}`);
      }
      // Invalidate all lottery-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['pointsLottery'] });
      queryClient.invalidateQueries({ queryKey: ['points', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['userPoints'] });
      // Force refetch of current page data
      queryClient.refetchQueries({ queryKey: ['pointsLottery'] });
    },
    onError: (error) => {
      // Always show error toasts
      toast.error(error instanceof Error ? error.message : 'Failed to claim reward');
    },
  });
}

export function useSeasonResults(seasonId: number, enabled = true) {
  return useQuery<SeasonResultsResponse>({
    queryKey: ['pointsLottery', 'seasonResults', seasonId],
    queryFn: () => pointsLotteryApi.getSeasonResults(seasonId),
    enabled: enabled && seasonId > 0,
    staleTime: 60_000, // 1 minute
    gcTime: 10 * 60_000, // 10 minutes
    retry: 2,
  });
}

export function useMintSeasonNFT() {
  const queryClient = useQueryClient();
  const { svmSendTransaction } = useSvmSendTransaction();

  return useMutation<NFTMintResponse, Error, { seasonNo?: number; previewTier?: NFTTier }>({
    mutationFn: async ({ seasonNo, previewTier }) => {
      const isMobile = isMobileWallet();
      logger.log(`🎨 [MintNFT] Platform: ${isMobile ? 'Mobile' : 'Desktop'}, Season: ${seasonNo ?? 'current'}`);

      // Step 1: Create NFT mint order using /chest/mint-nft endpoint
      logger.log('📝 [MintNFT] Creating mint order...');
      const orderResponse = await pointsLotteryApi.mintChestNFT({ season_no: seasonNo });
      logger.log('[MintNFT] Order created:', orderResponse.order_id);

      // Extract tier info from order metadata
      const { order_id, metadata } = orderResponse;
      const { nftTier, rank, season_no, points } = metadata;

      // Use preview tier if provided to prevent UI flickering
      const displayTier = previewTier || (nftTier?.toLowerCase() as NFTTier);
      logger.log('[MintNFT] Tier:', displayTier, 'Rank:', rank);

      // Step 2: Generate signature for the transaction (backend creates tx with blockhash)
      // IMPORTANT: This step should be as close to signing as possible to prevent blockhash expiration
      logger.log('🔐 [MintNFT] Generating signature (getting fresh blockhash)...');
      const signatureResponse = await pointsLotteryApi.generateSignature(order_id);
      logger.log('✅ [MintNFT] Signature ready');

      // Step 3: Deserialize the transaction
      const txBuffer = Buffer.from(signatureResponse.serialized_tx, 'hex');
      const tx = Transaction.from(txBuffer);

      // Step 4: Send transaction (wallet will sign and send)
      // Use preSigned: true because the backend already partially signed this transaction
      // For mobile: Show more urgent message as blockhash can expire if user is slow
      if (isMobile) {
        toast.info('Please approve quickly in your wallet app...', { duration: 5000 });
      } else {
        toast.info('Please approve the transaction in your wallet...');
      }

      logger.log('✍️ [MintNFT] Requesting wallet signature...');
      const result = await svmSendTransaction(tx, { preSigned: true });
      logger.log('✅ [MintNFT] Transaction sent:', result?.signature?.slice(0, 16) + '...');

      // Step 5: Get the signature from the transaction result
      if (!result?.signature) {
        throw new Error('Transaction signature not found');
      }

      // Extract NFT mint address from transaction accounts
      // The nftMint is typically one of the accounts in the transaction
      const nftMintPubkey = tx.instructions[tx.instructions.length - 1]?.keys[5]?.pubkey;
      if (!nftMintPubkey) {
        throw new Error('NFT mint address not found in transaction');
      }

      const mintAddress = nftMintPubkey.toBase58();
      logger.log('🎨 [MintNFT] NFT Mint Address:', mintAddress);

      // Step 6: Confirm the mint on the backend
      logger.log('📡 [MintNFT] Confirming with backend...');
      await pointsLotteryApi.confirmNftMint({
        orderId: order_id,
        signature: result.signature,
        nftMint: mintAddress,
      });
      logger.log('🎉 [MintNFT] Complete!');

      // Return complete NFTMintResponse using display tier (preview tier if available)
      return {
        seasonNo: season_no,
        tier: displayTier,
        rank: rank || null,
        pointsContributed: points || '0',
        mintAddress,
        orderId: order_id,
      };
    },
    onSuccess: (data) => {
      toast.success(`NFT minted successfully! Tier: ${data.tier.toUpperCase()}`);
      // Invalidate queries to refresh NFT-related data
      queryClient.invalidateQueries({ queryKey: ['pointsLottery', 'nft'] });
      queryClient.invalidateQueries({ queryKey: ['pointsLottery', 'unclaimed'] });
      queryClient.invalidateQueries({ queryKey: ['pointsLottery', 'seasonResults'] });
    },
    onError: (error) => {
      console.error('[useMintSeasonNFT] Error details:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to mint NFT';

      // First, check for insufficient SOL errors using the parser
      const parsedError = parseSolanaError(errorMessage);
      if (parsedError.type === 'insufficient_sol' && parsedError.details) {
        const { haveSOL, needSOL, shortfallSOL } = parsedError.details;
        toast.error(
          `Insufficient SOL to mint NFT. You have ${haveSOL.toFixed(4)} SOL but need ${needSOL.toFixed(4)} SOL. ` +
          `Please add at least ${shortfallSOL.toFixed(4)} SOL to your wallet.`,
          { duration: 8000 }
        );
        return;
      }

      // Check for specific error messages and provide helpful feedback
      if (errorMessage.includes('already minted') || errorMessage.includes('account already initialized')) {
        toast.error('You have already minted an NFT for this season. Each wallet can only mint one NFT per season.');
      } else if (errorMessage.includes('must contribute points') || errorMessage.includes('did not participate') || errorMessage.includes('Visit the Points Lottery')) {
        toast.error('You must contribute points to this season before you can mint an NFT. Visit the Points Lottery to contribute!');
      } else if (errorMessage.includes('no rank found') || errorMessage.includes('Leaderboard rankings are still being calculated') || errorMessage.includes('Rankings are updated')) {
        toast.error('Leaderboard rankings are still being calculated. Please wait a moment and try again. Rankings are updated every few minutes.');
      } else if (errorMessage.toLowerCase().includes('insufficient') || errorMessage.toLowerCase().includes('not enough')) {
        // Catch-all for other insufficient balance errors that don't match the exact pattern
        toast.error(
          'Insufficient SOL balance to complete this transaction. Please add more SOL to your wallet and try again.',
          { duration: 6000 }
        );
      } else {
        toast.error(errorMessage);
      }
    },
    retry: false,
  });
}

export function useUnclaimedRewards() {
  const { address } = useWalletAddress();

  return useQuery<UnclaimedRewardsResponse>({
    queryKey: ['pointsLottery', 'unclaimed', address ?? 'anon'],
    queryFn: () => pointsLotteryApi.getUnclaimedRewards(),
    enabled: !!address,
    staleTime: 30_000, // 30 seconds
    gcTime: 5 * 60_000, // 5 minutes
    retry: 2,
  });
}

export function useUserLotteryNFTs() {
  const { address } = useWalletAddress();
  const { isAuthenticated } = useAuthContext();

  return useQuery<UserNFTsResponse>({
    queryKey: ['pointsLottery', 'nft', 'user', address ?? 'anon'],
    queryFn: () => pointsLotteryApi.getUserLotteryNFTs(),
    enabled: isAuthenticated && !!address, // Check both authentication and wallet connection
    staleTime: 60_000, // 1 minute
    gcTime: 10 * 60_000, // 10 minutes
    retry: 2,
  });
}

export function useVerifyNFTsOnChain(nfts: UserNFTsResponse) {
  return useQuery({
    queryKey: ['pointsLottery', 'nft', 'verification', nfts.map(n => n.mintAddress).join(',')],
    queryFn: async () => {
      // Import dynamically to avoid SSR issues
      const { batchVerifyNFTs } = await import('@/utils/solana/nftVerification');

      // Filter out NFTs without mint addresses
      const mintAddresses = nfts
        .filter(nft => nft.mintAddress && nft.mintAddress !== null)
        .map(nft => nft.mintAddress as string);

      if (mintAddresses.length === 0) {
        return new Map();
      }

      return batchVerifyNFTs(mintAddresses);
    },
    enabled: nfts.length > 0,
    staleTime: 5 * 60_000, // 5 minutes - on-chain data doesn't change often
    gcTime: 10 * 60_000,
    retry: 1,
  });
}

export function useGenerateShareResult() {
  const queryClient = useQueryClient();

  return useMutation<ShareResultResponse, Error, { seasonId: number }>({
    mutationFn: async ({ seasonId }) => {
      const response = await pointsLotteryApi.generateShareResult({ seasonId });
      return response;
    },
    onSuccess: () => {
      // Invalidate share-related queries if needed
      queryClient.invalidateQueries({ queryKey: ['pointsLottery', 'share'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to generate share result');
    },
    retry: false,
  });
}
